/* Screen 3 - Review & Confirm */

import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import forwardarrow from "../../../../assets/images/arrow_forwardb.svg";
import goldIcon from "../../../../assets/images/Gold.svg";
import platiniumIcon from "../../../../assets/images/Platinium.svg";
import silverIcon from "../../../../assets/images/Silver.svg";
import Button from "../../../../components/common/Button/Button";
import SummaryContent from "../../../../components/common/SummaryContent/SummaryContent";
import TravelDetailsTable from "../../../../components/common/TravelDetailsTable/TravelDetailsTable";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useTravelRenewalFlow, {
  TRAVEL_RENEWAL_SELECTED_COVERS,
  TRAVEL_RENEWAL_SELECTED_PLAN,
} from "../../../../hooks/useTravelRenewalFlow";
import { PolicyViewResponse } from "../../../../redux/features/motor/getQuoteSlice";
import { selectNewMotorForm } from "../../../../redux/features/newMotorFormSlice";
import {
  clearPolicyPremium,
  policyPremium,
} from "../../../../redux/features/motor/renewalPremiumSlice";
import {
  getPolicyPayLoading,
  policyPayAddCovers,
} from "../../../../redux/features/payment/policyPaySlice";
import { getClientSource } from "../../../../utils/getClientSource";
import {
  applyPromoCodeToQuote,
  calculatePromoDiscount,
  getFinalQuoteNumberForPayment,
  getFinalPremiumForPayment,
} from "../../../../utils/promoCodeUtils";
import TravelRenewalLayout from "../components/TravelRenewalLayout";

// Plan code to display info mapping
// Old codes (SILVER/GO/PL) kept for existing policies, new codes for new travel plans
const travelPlanMap = {
  SILVER: {
    image: silverIcon,
    text: "Silver",
    text_ar: "الفضية",
  },
  GO: {
    image: goldIcon,
    text: "Gold",
    text_ar: "الذهبية",
  },
  PL: {
    image: platiniumIcon,
    text: "Platinum",
    text_ar: "البلاتينية",
  },
  SCHENGENB: {
    image: silverIcon,
    text: "Schengen",
    text_ar: "شنغن",
  },
  WLEXB: {
    image: goldIcon,
    text: "Worldwide (Excl. USA & Canada)",
    text_ar: "جميع دول العالم (باستثناء أمريكا وكندا)",
  },
  WLB: {
    image: platiniumIcon,
    text: "Worldwide",
    text_ar: "جميع دول العالم",
  },
};

const ReviewConfirmPage = ({ handleNavigationToRenewalHub }) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(0);
  const [promoAppliedQuoteData, setPromoAppliedQuoteData] = useState(null);
  const [appliedPromoCode, setAppliedPromoCode] = useState("");
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  // Renewal premium from the premium API (POST /policy/premium)
  const [premiumData, setPremiumData] = useState(null);
  const [premiumLoading, setPremiumLoading] = useState(true);
  const [premiumError, setPremiumError] = useState(null);

  // Flow hook
  const { goToStep, planChanged } = useTravelRenewalFlow();

  // Policy info from Redux
  const policyInfo = useSelector(PolicyViewResponse);
  const policyPayLoading = useSelector(getPolicyPayLoading);

  // Get selected plan from Redux (if user changed plan)
  const selectedPlan = useSelector(selectNewMotorForm)?.[TRAVEL_RENEWAL_SELECTED_PLAN];

  // Get selected covers from Redux
  const selectedCovers = useSelector(selectNewMotorForm)?.[TRAVEL_RENEWAL_SELECTED_COVERS] || {
    coverOptionsState: [],
  };

  // Current plan code from policy
  const currentPlanCode = policyInfo?.PolicyHeader?.PlanCode;

  // New plan code (if changed) or current plan
  const newPlanCode = planChanged && selectedPlan?.code ? selectedPlan.code : currentPlanCode;

  // Get included covers (newly added, not default)
  const includedCoversAll =
    selectedCovers?.coverOptionsState?.filter(
      (cover) => cover.included === true && cover.defaultIncludedInPlan === false
    ) ?? [];

  const includedCovers =
    selectedCovers?.coverOptionsState
      ?.filter(
        (cover) => cover.included === true && cover.defaultIncludedInPlan === false
      )
      ?.map((cover) => cover.coverCode || cover.code) ?? [];

  const includedCoversString = includedCovers.join(",");

  // Whether a given optional cover is currently included in the selection
  const isCoverIncluded = (code) =>
    selectedCovers?.coverOptionsState?.some(
      (c) => (c.coverCode || c.code) === code && c.included === true
    ) || false;

  // Resolve the NewPlan to send to the premium API.
  // Legacy policies (SILVER/GO/PL) MUST renew on a new plan; new-plan policies
  // only send NewPlan when the user switched plan.
  const LEGACY_PLAN_CODES = ["SILVER", "GO", "PL"];
  const isLegacyPlan = LEGACY_PLAN_CODES.includes(currentPlanCode);
  const newPlanForApi =
    planChanged && selectedPlan?.code
      ? selectedPlan.code
      : isLegacyPlan
      ? selectedPlan?.code
      : undefined;

  // Fetch the renewal premium from the premium API once plan + covers are chosen.
  // CPER137 (Summer Activities) -> WaterSport, CPER17 (Winter Activities) -> WinterSport.
  const policyNumberForPremium = policyInfo?.PolicyHeader?.PolicyNumber;
  const waterSport = isCoverIncluded("CPER137");
  const winterSport = isCoverIncluded("CPER17");
  useEffect(() => {
    if (!policyNumberForPremium) return;
    let cancelled = false;
    const fetchPremium = async () => {
      setPremiumLoading(true);
      setPremiumError(null);
      try {
        const payload = {
          PolicyNumber: policyNumberForPremium,
          Channel: getClientSource(),
          ...(newPlanForApi ? { NewPlan: newPlanForApi } : {}),
          WaterSport: waterSport,
          WinterSport: winterSport,
        };
        const action = await dispatch(policyPremium(payload));
        const response = unwrapResult(action);
        if (!cancelled) setPremiumData(response);
      } catch (err) {
        if (!cancelled) {
          setPremiumError(
            err?.ErrorMessageEn || err?.message || "Failed to load renewal premium."
          );
        }
      } finally {
        if (!cancelled) setPremiumLoading(false);
      }
    };
    fetchPremium();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    policyNumberForPremium,
    newPlanForApi,
    waterSport,
    winterSport,
  ]);

  // Clear the premium slice when leaving the screen
  useEffect(() => {
    return () => {
      dispatch(clearPolicyPremium());
    };
  }, [dispatch]);

  // Surface premium API failures as a toast
  useEffect(() => {
    if (premiumError) {
      toast.error(
        translate(
          "Failed to load the renewal premium. Please try again.",
          "فشل تحميل قسط التجديد. يرجى المحاولة مرة أخرى."
        )
      );
    }
  }, [premiumError, translate]);

  // Pricing — all values come from the premium API response
  const hasPremium = !!premiumData && !premiumError;
  const apiPremium = Number(premiumData?.Premium) || 0;
  const VAT = Number(premiumData?.Tax) || 0;
  const apiTotalPremium = Number(premiumData?.TotalPremium) || apiPremium + VAT;
  const apiSumInsured = premiumData?.SumInsured;
  // New proposal number returned by the premium API (used as the renewal quote ref)
  const renewalQuoteNumber =
    premiumData?.PolicyNumber ||
    premiumData?.QuoteNumber ||
    policyInfo?.PolicyHeader?.PolicyNumber;

  const totalPrice = apiTotalPremium;
  const basePremium = hasPremium ? apiPremium : "N/A";
  const totalPriceDisplay = hasPremium ? apiTotalPremium : "N/A";

  // Build travel data for TravelDetailsTable
  const buildTravelData = () => {
    const data = {};

    // Passenger 1 is always the policy holder (Party) — that's where we have the
    // full info. For family plans the Passengers array holds the OTHER members
    // only (the holder is excluded), so they follow as Passenger 2, 3, ...
    const party = policyInfo?.PolicyHeader?.Party;
    const partyName = `${party?.FirstName || ""} ${party?.LastName || ""}`.trim();
    data.passenger1 = partyName || "N/A";

    // Additional members (family plans). Skip empty/placeholder entries that
    // have no name so individual plans don't show a stray "Passenger 2 — N/A".
    const passengers = policyInfo?.PolicyHeader?.Passengers || [];
    let extraCount = 0;
    passengers.forEach((passenger) => {
      const name =
        passenger?.FullName ||
        passenger?.fullName ||
        passenger?.name ||
        `${passenger?.FirstName || ""} ${passenger?.LastName || ""}`.trim();
      if (!name) return;
      extraCount += 1;
      data[`passenger${extraCount + 1}`] = name;
    });

    // Add departure and return dates
    if (policyInfo?.PolicyHeader?.FromDateStr) {
      data.departureDate = policyInfo.PolicyHeader.FromDateStr;
    }
    if (policyInfo?.PolicyHeader?.ToDateStr) {
      data.returnDate = policyInfo.PolicyHeader.ToDateStr;
    }

    // Add plan info
    data.plan = translate(
      travelPlanMap[newPlanCode]?.text ||
        selectedPlan?.name ||
        policyInfo?.PolicyHeader?.PlanName ||
        "N/A",
      travelPlanMap[newPlanCode]?.text_ar ||
        selectedPlan?.name_ar ||
        policyInfo?.PolicyHeader?.PlanName ||
        "غير متوفر"
    );

    // Add add-ons (newly selected covers) — Winter Sport (CPER17) first, Terrorism (CPER137) second
    if (includedCoversAll && includedCoversAll.length > 0) {
      const orderMap = { CPER17: 1, CPER137: 2 };
      data.addons = [...includedCoversAll]
        .sort((a, b) => {
          const codeA = a.coverCode || a.code;
          const codeB = b.coverCode || b.code;
          return (orderMap[codeA] || 999) - (orderMap[codeB] || 999);
        })
        .map((cover) => translate(cover.name, cover.name_ar));
    }

    return data;
  };

  const travelData = buildTravelData();

  // Build planData for SummaryContent component
  // When a promo code is applied, swap in values returned from /policy/applypromo
  const promoPremium =
    promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Premium
      : basePremium;
  const promoTax =
    promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Tax
      : hasPremium
      ? VAT
      : "N/A";
  const promoTotal =
    promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.TotalPremium
      : totalPriceDisplay;

  const planData = {
    basePremium: promoPremium,
    premium: promoPremium,
    tax: promoTax,
    totalPremium: promoTotal,
    sumInsured: apiSumInsured,
    quoteNumber: renewalQuoteNumber || "N/A",
    policyNumber: renewalQuoteNumber || "N/A",
    Covers: includedCoversAll.map((cover) => {
      const code = cover.coverCode || cover.code;
      return {
        coverCode: code,
        coverName:
          code === "CPER17"
            ? "Winter Activities"
            : code === "CPER137"
            ? "Summer Activities"
            : cover.name,
        Price: Number(cover.price) || 0,
      };
    }),
    getCoverPriceByCode: (coverCode) => {
      const cover = includedCoversAll.find(
        (c) => (c.coverCode || c.code) === coverCode
      );
      return cover ? Number(cover.price) || 0 : 0;
    },
  };

  // Promo code handlers
  // Editing the input after a successful apply invalidates the applied state
  const handlePromoCodeChange = (value) => {
    setPromoCode(value);
    if (promoCodeApplied && value !== appliedPromoCode) {
      setPromoCodeApplied(false);
      setPromoCodeDiscount(0);
      setPromoAppliedQuoteData(null);
      setAppliedPromoCode("");
    }
  };

  const applyPromoCode = async () => {
    // Gate promo until the renewal premium has loaded
    if (!hasPremium || !totalPrice) {
      toast.error(
        translate(
          "Please wait for renewal pricing to load before applying a promo code.",
          "يرجى الانتظار حتى يتم تحميل سعر التجديد قبل تطبيق كود الخصم."
        )
      );
      return;
    }

    setIsApplyingPromo(true);

    try {
      const quoteNumber = renewalQuoteNumber;
      const trimmedCode = (promoCode || "").trim();
      const result = await applyPromoCodeToQuote(quoteNumber, trimmedCode, translate);

      if (result.success) {
        const discount = calculatePromoDiscount(
          totalPrice,
          result.data.TotalPremium
        );

        setPromoCodeApplied(true);
        setPromoCodeDiscount(discount);
        setPromoAppliedQuoteData(result.data);
        setAppliedPromoCode(trimmedCode);

        toast.success(
          translate("Promo code applied!", "تم تطبيق كود الخصم!")
        );
      } else {
        setPromoCodeApplied(false);
        setPromoCodeDiscount(0);
        setPromoAppliedQuoteData(null);
        setAppliedPromoCode("");
        toast.error(result.error);
      }
    } catch (error) {
      console.error("Promo code application exception:", error);
      setPromoCodeApplied(false);
      setPromoCodeDiscount(0);
      setPromoAppliedQuoteData(null);
      setAppliedPromoCode("");
      toast.error(
        translate(
          "Failed to apply promo code. Please try again.",
          "فشل تطبيق كود الخصم. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoCodeApplied(false);
    setPromoCodeDiscount(0);
    setPromoAppliedQuoteData(null);
    setAppliedPromoCode("");
  };

  // Handle back button - go to Enhance Coverage
  const handleBackClick = () => {
    goToStep(2); // Go to Enhance Coverage
  };

  // Handle payment
  const handlePayment = async () => {
    // The premium API returns a new proposal number — pay against that quote.
    const finalPolicyNumber = getFinalQuoteNumberForPayment(
      promoCodeApplied,
      promoAppliedQuoteData,
      renewalQuoteNumber ?? ""
    );
    const finalPremium = getFinalPremiumForPayment(
      promoCodeApplied,
      promoAppliedQuoteData,
      totalPrice
    );

    const payload = {
      PolicyNumber: finalPolicyNumber,
      Premium: finalPremium ?? "",
      Channel: getClientSource(),
      Covers: includedCoversString ?? "",
      NewPlan: newPlanForApi ?? "",
      uatAmount: `${finalPremium}` ?? "",
      ...(promoCodeApplied && appliedPromoCode ? { PromoCode: appliedPromoCode } : {}),
    };

    try {
      const linkAction = await dispatch(policyPayAddCovers(payload));
      const link = unwrapResult(linkAction);

      if (link) {
        window.open(link, "_self");
      }
    } catch (error) {
      toast.error(
        translate(
          "Error in getting payment link",
          "خطأ في الحصول على رابط الدفع"
        )
      );
    }
  };

  return (
    <TravelRenewalLayout
      is_hide_back_btn={false}
      show_close_btn={false}
      isFirstStep={false}
      backClickHandler={handleBackClick}
      is_bottom_sticky_layout={false}
      show_summary={false}
    >
      <div className="flex flex-col items-center mx-auto max-w-lg max767:p-4 pt-[0%]">
        <div className="flex justify-center items-center w-full">
          <div className="p-0 w-full">
            <div className="max767:mt-2">
              <TitleDescription
                title={translate("Review and Confirm", "المراجعة والتأكيد")}
                description={translate(
                  "Please review your information and proceed to payment.",
                  "يرجى مراجعة معلوماتك والانتقال إلى الدفع."
                )}
              />
            </div>

            {/* Policy Section */}
            <div className="mb-8">
              {/* Travel Details Table */}
              <div className="bg-[#F6F8F9] max767:bg-white rounded-xl p-4 max767:p-0 mb-6">
                <h2 className="text-base font-semibold text-[#1A2024] mb-2">
                  {translate(
                    "Travel Insurance Policy Renewal",
                    "تجديد سياسة تأمين السفر"
                  )}
                </h2>
                <p className="text-sm mb-4 text-[#1A2024]">
                  {translate(
                    "Review the details below and confirm the payment",
                    "راجع التفاصيل أدناه وأكد الدفع"
                  )}
                </p>
                <TravelDetailsTable travelData={travelData} />
              </div>
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="mb-8 w-full">
          <div className="bg-[#F6F8F9] rounded-xl p-4">
            <SummaryContent
              planData={promoCodeApplied && promoAppliedQuoteData ? { ...planData, premium: promoAppliedQuoteData.Premium ?? planData.premium, tax: promoAppliedQuoteData.Tax ?? planData.tax, totalPremium: promoAppliedQuoteData.TotalPremium ?? planData.totalPremium, quoteNumber: promoAppliedQuoteData.PolicyNumber ?? planData.quoteNumber } : planData}
              promoCode={promoCode}
              setPromoCode={handlePromoCodeChange}
              applyPromoCode={applyPromoCode}
              clearPromoCode={clearPromoCode}
              promoCodeApplied={promoCodeApplied}
              promoCodeDiscount={promoCodeDiscount}
              isApplyingPromo={isApplyingPromo}
              showQuotationNumber={true}
              showPromoCode={true}
              className="!bg-transparent"
              flowType="travel"
            />
          </div>
        </div>

        {/* Policy Notice */}
        <div className="mb-6 p-4 bg-[#FDE9D2] border border-[#FBC98E] rounded-lg flex items-start space-x-3 rtl:space-x-reverse w-full">
          <div className="flex-shrink-0 mt-0.5">
            <svg
              className="w-5 h-5 text-[#A56213]"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <p className="text-[#252C32] font-semibold text-sm">
              {translate(
                "Kindly note that the policy will be issued under your name",
                "يرجى ملاحظة أن الوثيقة ستصدر باسمك"
              )}
            </p>
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="mb-6 w-full max767:!pb-20">
          <label className="flex items-start space-x-3 rtl:space-x-reverse cursor-pointer">
            <label className="flex items-center cursor-pointer gap-2">
              <input
                type="checkbox"
                className="peer hidden"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              <div
                className="size-5 border-2 border-gray-400 rounded-[4px]
                  peer-checked:bg-[#00A9E7] peer-checked:border-[#00A9E7]
                  flex items-center justify-center transition-all"
              >
                <svg
                  width="12"
                  height="9"
                  viewBox="0 0 12 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11 0C10.72 0 10.47 0.11 10.29 0.29L4 6.59L1.71 4.29C1.53 4.11 1.28 4 1 4C0.45 4 0 4.45 0 5C0 5.28 0.11 5.53 0.29 5.71L3.29 8.71C3.47 8.89 3.72 9 4 9C4.28 9 4.53 8.89 4.71 8.71L11.71 1.71C11.89 1.53 12 1.28 12 1C12 0.45 11.55 0 11 0Z"
                    fill="white"
                  />
                </svg>
              </div>
            </label>
            <div className="text-sm text-[#252C32] rtl:font-medium leading-[24px] text-justify">
              <span>
                {translate(
                  "I hereby certify that the above given information is true and correct to the best of my knowledge, and I agree to cooperate fully by providing all requested documents before payment. I understand that providing invalid information will result in my insurance policy to be void. I also read and agree the",
                  "أشهد بموجب هذا أن المعلومات المقدمة أعلاه صحيحة ودقيقة حسب أفضل ما أعلم، وأوافق على التعاون الكامل من خلال تقديم جميع الوثائق المطلوبة قبل الدفع. أفهم أن تقديم معلومات غير صحيحة سيؤدي إلى إبطال وثيقة التأمين الخاصة بي. كما أنني قرأت وأوافق على"
                )}
              </span>{" "}
              <button
                onClick={() =>
                  window.open(
                    "https://content.solidarity.bh/uploads/TRAVEL_INSURANCE_WORDING_30_08_2023_8e74afd369_44e9596879.pdf",
                    "_blank"
                  )
                }
                className="text-[#00384D] underline hover:text-[#0088CC] font-bold"
              >
                {translate("Policy Specification", "مواصفات وثيقة")}
              </button>{" "}
              <span>
                {translate("of this Insurance product.", "التأمين هذه.")}
              </span>
            </div>
          </label>
        </div>

        {/* Proceed Button - Fixed at bottom on mobile */}
        <div className="max767:footer max767:bg-white max767:border-t-[#E5E9EB] max767:border-t max767:border-solid max767:mt-2 max767:py-4 flex max767:flex-col !gap-4 mt-4 max767:fixed max767:bottom-0 max767:p-4 max767:inset-x-0 ipad:items-center max767:inline-block max767:z-[9] w-full">
          <Button
            isLoading={policyPayLoading || premiumLoading}
            disabled={!agreedToTerms || !hasPremium || premiumLoading}
            className={`bg-[#55C6EF] mt-4 mb-9 max767:mt-0 max767:mb-0 w-full text-[#1A2024] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] flex justify-center items-center p-3 rounded-[10px] cursor-pointer max767:h-[40px] ${
              !agreedToTerms || !hasPremium ? "cursor-not-allowed" : ""
            }`}
            fontSize="text-lg max767:text-sm"
            label={translate("Proceed to Payment", "المتابعة للدفع")}
            onSubmit={handlePayment}
            post_icon={forwardarrow}
            post_icon_class={"w-6 h-6 ml-[5px] rtl:rotate-180 rtl:mr-2"}
          />
        </div>
      </div>
    </TravelRenewalLayout>
  );
};

export default ReviewConfirmPage;
